import { Component, EventEmitter, Input, Output, OnDestroy } from "@angular/core";
import { Subscription } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import Swal from "sweetalert2";
import { isRichTextEmpty } from "../shared/rich-text.utils";

@Component({
  selector: "app-tab-files",
  templateUrl: "./tab-files.component.html",
  styleUrls: ["./tab-files.component.scss"],
})
export class TabFilesComponent implements OnDestroy {
  private readonly MAX_SUBSECTIONS_PER_SECTION = 12;
  private readonly FILE_UPLOAD_MAX_RETRIES = 3;
  private readonly FILE_UPLOAD_RETRY_DELAY_MS = 1200;
  private activeUploads = new Map<string, Subscription>();

  get maxSubsectionsPerSection(): number {
    return this.MAX_SUBSECTIONS_PER_SECTION;
  }

  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};
  @Input() allFileSectionsExpanded: boolean = false;
  @Input() deleteSection: any[] = [];
  @Input() deleteSubSection: any[] = [];
  @Input() deleteMedia: any[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() deleteSectionChange = new EventEmitter<any[]>();
  @Output() deleteSubSectionChange = new EventEmitter<any[]>();
  @Output() deleteMediaChange = new EventEmitter<any[]>();

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private heicConverter: HeicConverterService
  ) {}

  ngOnInit(): void {
    if (this.model.fileSections && this.model.fileSections.length > 0) {
      this.model.fileSections.forEach((section, index) => {
        section.collapsed = false;
        if (section.display === undefined) {
          section.display = true;
        }
        if (section.index === undefined) {
          section.index = index;
        }
        if (section.subSections) {
          section.subSections.forEach((subsection, subIndex) => {
            subsection.collapsed = false;
            if (subsection.display === undefined) {
              subsection.display = true;
            }
            if (subsection.index === undefined) {
              subsection.index = subIndex;
            }
          });
        }
      });

      // Cập nhật checkbox chính dựa trên trạng thái của các sections sau khi khởi tạo
      this.updateMainDisplayCheckbox();
    } else {
      // Nếu không có section nào:
      // - Nếu user đã check displayFiles = true, giữ nguyên để validation có thể báo lỗi
      // - Chỉ untick nếu displayFiles = false (chưa được check)
      if (this.model.displayFiles === false) {
        this.model.displayFiles = false;
      }
      // Nếu displayPhotos = true (user đã check), giữ nguyên để validation báo lỗi khi publish
    }
    this.allFileSectionsExpanded = true;
  }

  checkAndSetFileDisplay() {
    if (!this.model.displayFiles) {
      // Check if there's any file content
      if (
        this.model.titleFileSections ||
        (this.model.fileSections &&
          this.model.fileSections.length > 0 &&
          this.model.fileSections.some(
            section =>
              section.name ||
              (section.subSections &&
                section.subSections.some(
                  sub =>
                    sub.title ||
                    sub.pageTitle ||
                    !isRichTextEmpty(sub.description) ||
                    (sub.media && sub.media.length > 0)
                ))
          ))
      ) {
        this.model.displayFiles = true;
      }
    }
    this.modelChange.emit(this.model);
  }

  toggleExpandAllFileSections() {
    this.allFileSectionsExpanded = !this.allFileSectionsExpanded;
    this.model.fileSections.forEach(section => {
      section.collapsed = !this.allFileSectionsExpanded;
      section.subSections.forEach(subsection => {
        subsection.collapsed = !this.allFileSectionsExpanded;
      });
    });
  }

  addFileSection() {
    // init maximum 5 section
    // if (this.model.fileSections.length >= 5) {
    //   this.toastr.warning("Maximum 5 sections are allowed");
    //   return;
    // }

    this.model.fileSections = this.model.fileSections || [];
    const nextSectionIndex =
      this.model.fileSections.length === 0 ? 0 : Math.max(...this.model.fileSections.map(s => s.index || 0)) + 1;
    const newSection = {
      name: "",
      collapsed: false,
      display: false,
      index: nextSectionIndex,
      subSections: [
        {
          title: "",
          pageTitle: "",
          description: "",
          media: [],
          collapsed: false,
          display: false,
          index: 0,
        },
      ],
    };
    this.model.fileSections.push(newSection);
    // Chỉ section mới mở (collapsed: false đã set ở newSection); giữ nguyên trạng thái đóng/mở của các section/subsection cũ

    // Cập nhật checkbox chính dựa trên trạng thái của các sections
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deleteFileSection(sectionIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the entire section and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const deleSection = this.model.fileSections[sectionIndex];
        if (deleSection && deleSection.id) {
          this.deleteSection.push(deleSection.id);
          this.deleteSectionChange.emit(this.deleteSection);
        }
        this.model.fileSections.splice(sectionIndex, 1);
        this.modelChange.emit(this.model);
      }
    });
  }

  deleteFileSubsection(sectionIndex: number, subIndex: number) {
    Swal.fire({
      title: "Are you sure?",
      text: "This will delete the subsection and all its content.",
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const deleSubSection = this.model.fileSections[sectionIndex].subSections[subIndex];
        if (deleSubSection && deleSubSection.id) {
          this.deleteSubSection.push(deleSubSection.id);
          this.deleteSubSectionChange.emit(this.deleteSubSection);
        }
        this.model.fileSections[sectionIndex].subSections.splice(subIndex, 1);
        this.modelChange.emit(this.model);
      }
    });
  }

  toggleSectionCollapse(section: any) {
    section.collapsed = !section.collapsed;
  }

  toggleSubsectionCollapse(subsection: any) {
    subsection.collapsed = !subsection.collapsed;
  }

  onDisplayFilesChange(value: boolean) {
    this.model.displayFiles = value;
    // Tick/untick tất cả sections và subsections
    if (this.model.fileSections && this.model.fileSections.length > 0) {
      this.model.fileSections.forEach(section => {
        section.display = value;
        if (section.subSections) {
          section.subSections.forEach(subsection => {
            subsection.display = value;
          });
        }
      });
    }
    this.modelChange.emit(this.model);
  }

  onSectionDisplayChange(sectionIndex: number, value: boolean) {
    const section = this.model.fileSections[sectionIndex];
    if (section) {
      section.display = value;

      // Tick/untick tất cả subsection trong section này
      if (section.subSections) {
        section.subSections.forEach(subsection => {
          subsection.display = value;
        });
      }

      // Tự động tick/untick checkbox chính dựa trên trạng thái của các sections
      const oldDisplayFiles = this.model.displayFiles;
      this.updateMainDisplayCheckbox();

      this.modelChange.emit(this.model);
    }
  }

  /**
   * Cập nhật checkbox chính (displayFiles) dựa trên trạng thái của các sections
   * - Nếu có ít nhất 1 section được tick → tick checkbox chính
   * - Nếu tất cả sections đều untick → untick checkbox chính
   */
  private updateMainDisplayCheckbox() {
    if (!this.model.fileSections || this.model.fileSections.length === 0) {
      // Nếu không có section nào:
      // - Nếu user đã check displayFiles = true, giữ nguyên để validation có thể báo lỗi
      // - Chỉ untick nếu displayFiles = false (chưa được check)
      if (this.model.displayFiles === false) {
        this.model.displayFiles = false;
      }
      // Nếu displayPhotos = true (user đã check), giữ nguyên để validation báo lỗi khi publish
      return;
    }

    const hasAnySectionSelected = this.model.fileSections.some(section => section.display === true);

    // Cập nhật checkbox chính - chỉ thay đổi nếu giá trị khác
    if (this.model.displayFiles !== hasAnySectionSelected) {
      this.model.displayFiles = hasAnySectionSelected;
      // Emit để đảm bảo parent component nhận được thay đổi
      this.modelChange.emit(this.model);
    } else {
    }
  }

  onSubsectionDisplayChange(sectionIndex: number, subIndex: number, value: boolean) {
    const section = this.model.fileSections[sectionIndex];
    if (section && section.subSections && section.subSections[subIndex]) {
      section.subSections[subIndex].display = value;

      // Nếu subsection được chọn, tự động chọn section
      if (value) {
        section.display = true;
      } else {
        // Nếu subsection bị unselect, kiểm tra xem còn subsection nào được chọn không
        const hasAnySubsectionSelected = section.subSections.some(sub => sub.display === true);
        // Nếu không còn subsection nào được chọn, unselect section
        if (!hasAnySubsectionSelected) {
          section.display = false;
        }
      }

      // Tự động tick/untick checkbox chính dựa trên trạng thái của các sections
      const oldDisplayFiles = this.model.displayFiles;
      this.updateMainDisplayCheckbox();

      this.modelChange.emit(this.model);
    }
  }

  dropSection(event: CdkDragDrop<any[]>) {
    moveItemInArray(this.model.fileSections, event.previousIndex, event.currentIndex);
    // Cập nhật index cho tất cả sections sau khi drag & drop
    // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
    this.model.fileSections.forEach((section, index) => {
      section.index = index;
    });
    this.modelChange.emit(this.model);
  }

  moveSectionUp(sectionIndex: number) {
    if (sectionIndex > 0 && this.model.fileSections && this.model.fileSections.length > 1) {
      // Hoán đổi vị trí section với section phía trên
      const temp = this.model.fileSections[sectionIndex];
      this.model.fileSections[sectionIndex] = this.model.fileSections[sectionIndex - 1];
      this.model.fileSections[sectionIndex - 1] = temp;

      // Cập nhật index cho tất cả sections
      // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
      this.model.fileSections.forEach((section, index) => {
        section.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSectionDown(sectionIndex: number) {
    if (
      sectionIndex < this.model.fileSections.length - 1 &&
      this.model.fileSections &&
      this.model.fileSections.length > 1
    ) {
      // Hoán đổi vị trí section với section phía dưới
      const temp = this.model.fileSections[sectionIndex];
      this.model.fileSections[sectionIndex] = this.model.fileSections[sectionIndex + 1];
      this.model.fileSections[sectionIndex + 1] = temp;

      // Cập nhật index cho tất cả sections
      // Lưu ý: sectionNumber không thay đổi, chỉ index thay đổi
      this.model.fileSections.forEach((section, index) => {
        section.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSubsectionUp(sectionIndex: number, subIndex: number) {
    console.log("moveSubsectionUp", sectionIndex, subIndex);
    const section = this.model.fileSections[sectionIndex];
    if (subIndex > 0 && section && section.subSections && section.subSections.length > 1) {
      // Hoán đổi vị trí subsection với subsection phía trên
      const temp = section.subSections[subIndex];
      section.subSections[subIndex] = section.subSections[subIndex - 1];
      section.subSections[subIndex - 1] = temp;

      // Cập nhật index cho tất cả subsections trong section này
      section.subSections.forEach((subsection, index) => {
        subsection.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  moveSubsectionDown(sectionIndex: number, subIndex: number) {
    console.log("moveSubsectionDown", sectionIndex, subIndex);
    const section = this.model.fileSections[sectionIndex];
    if (subIndex < section.subSections.length - 1 && section && section.subSections && section.subSections.length > 1) {
      // Hoán đổi vị trí subsection với subsection phía dưới
      const temp = section.subSections[subIndex];
      section.subSections[subIndex] = section.subSections[subIndex + 1];
      section.subSections[subIndex + 1] = temp;

      // Cập nhật index cho tất cả subsections trong section này
      section.subSections.forEach((subsection, index) => {
        subsection.index = index;
      });

      this.modelChange.emit(this.model);
    }
  }

  async uploadFileToSubsection(files: FileList, sectionIndex: number, subIndex: number, section: any, event?: any) {
    // Helper function to reset input
    const resetInput = () => {
      if (event && event.target) {
        event.target.value = "";
      }
    };

    if (!files || files.length === 0) {
      resetInput();
      return;
    }

    // Ensure model structure exists
    if (
      !this.model.fileSections ||
      !this.model.fileSections[sectionIndex] ||
      !this.model.fileSections[sectionIndex].subSections ||
      !this.model.fileSections[sectionIndex].subSections[subIndex]
    ) {
      this.toastr.error("Invalid section or subsection", "Error");
      resetInput();
      return;
    }

    const subsection = this.model.fileSections[sectionIndex].subSections[subIndex];

    // Initialize media array if it doesn't exist
    if (!subsection.media) {
      subsection.media = [];
    }

    // Check current subsection size
    const currentSubsectionSizeMB = this.getFileSizeInSubsection(sectionIndex, subIndex);

    // Calculate total size of new files
    let newFilesSizeMB = 0;
    Array.from(files).forEach((file: File) => {
      newFilesSizeMB += file.size / (1024 * 1024);
    });

    // Check if adding these files would exceed the 100MB limit per subsection
    if (currentSubsectionSizeMB + newFilesSizeMB > 100) {
      const remainingCapacityMB = Math.max(0, 100 - currentSubsectionSizeMB);
      this.toastr.error(
        `Maximum 100MB allowed per subsection. Current: ${currentSubsectionSizeMB.toFixed(2)}MB. You can only add ${remainingCapacityMB.toFixed(2)}MB more.`,
        "Error"
      );
      resetInput();
      return;
    }

    // Validate files first
    const validFiles: File[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      // Validate file type (PDF only)
      if (file.type !== "application/pdf") {
        this.toastr.error(`File ${file.name} must be a PDF`, "Error");
        continue;
      }

      // Validate individual file size (100MB limit for PDF)
      if (file.size / 1024 / 1024 > 100) {
        this.toastr.error(`File ${file.name} exceeds 100MB limit`, "Error");
        continue;
      }

      validFiles.push(file);
    }

    // If no valid files after validation, reset input and return
    if (validFiles.length === 0) {
      resetInput();
      return;
    }

    validFiles.sort((a, b) => a.name.localeCompare(b.name));

    // Upload files sequentially using for loop with async/await
    for (let index = 0; index < validFiles.length; index++) {
      const file = validFiles[index];

      try {
        // Generate unique identifiers for this specific upload
        const timestamp = Date.now();
        const uploadKey = this.generateUploadKey(
          "file",
          file.name,
          `section_${sectionIndex}_sub_${subIndex}_${timestamp}_${index}`
        );
        const tempId = `temp_${timestamp}_${index}_${Math.random().toString(36).substr(2, 9)}`;

        this.setUploadState(uploadKey, true);

        // Create media item immediately with unique identifiers
        const mediaItem = {
          type: "pdf",
          url: null,
          fileName: file.name,
          width: 0,
          height: 0,
          size: file.size,
          uploadKey: uploadKey,
          tempId: tempId,
          thumbnail: null,
        };

        // Add to subsection immediately
        subsection.media.push(mediaItem);

        // Upload file and wait for completion
        await this.uploadSingleFileToSubsection(file, uploadKey, mediaItem, subsection, "digital/files", 100);
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        // Error handling is done in uploadSingleFileToSubsection method
      }
    }

    // Sort toàn bộ media theo fileName
    if (subsection.media && subsection.media.length > 1) {
      subsection.media.sort((a, b) => (a.fileName || "").localeCompare(b.fileName || ""));
    }

    // Check and set file display after all uploads are done
    this.checkAndSetFileDisplay();

    // Reset input để lần sau chọn cùng file vẫn trigger
    resetInput();
  }

  addFileSubsection(sectionIndex: number) {
    const section = this.model.fileSections[sectionIndex];
    if (section.subSections.length >= this.MAX_SUBSECTIONS_PER_SECTION) {
      this.toastr.error(`Maximum ${this.MAX_SUBSECTIONS_PER_SECTION} subSections allowed per section`);
      return;
    }
    const nextSubIndex =
      section.subSections.length === 0 ? 0 : Math.max(...section.subSections.map(ss => ss.index || 0)) + 1;
    section.subSections.push({
      title: "",
      pageTitle: "",
      description: "",
      collapsed: false,
      media: [],
      display: false,
      index: nextSubIndex,
    });

    // Cập nhật checkbox chính dựa trên trạng thái của các sections
    this.updateMainDisplayCheckbox();
    this.modelChange.emit(this.model);
  }

  deleteAllFilesFromSubsection(sectionIndex: number, subIndex: number) {
    // Validate indices
    if (
      !this.model.fileSections ||
      !this.model.fileSections[sectionIndex] ||
      !this.model.fileSections[sectionIndex].subSections ||
      !this.model.fileSections[sectionIndex].subSections[subIndex]
    ) {
      console.error("Invalid section or subsection index for delete all files");
      return;
    }

    const subsection = this.model.fileSections[sectionIndex].subSections[subIndex];

    if (!subsection.media || subsection.media.length === 0) {
      this.toastr.info("No files to delete");
      return;
    }

    const fileCount = subsection.media.length;

    Swal.fire({
      title: "Are you sure?",
      text: `This will delete all ${fileCount} file(s) from this sub-section.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete all!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        // Collect media IDs that need to be deleted from database
        const mediaIdsToDelete = subsection.media
          .filter(media => media.id && typeof media.id === "number" && media.id > 0)
          .map(media => media.id);

        // Add to deleteMedia array for batch deletion during save
        if (mediaIdsToDelete.length > 0) {
          this.deleteMedia = this.deleteMedia.concat(mediaIdsToDelete);
          this.deleteMediaChange.emit(this.deleteMedia);
        }

        // Clear the media array immediately for better UX
        subsection.media = [];

        this.toastr.success(`All ${fileCount} file(s) deleted successfully`);

        this.modelChange.emit(this.model);
      }
    });
  }

  removeFileFromSubsection(sectionIndex: number, subIndex: number, mediaIndex: number) {
    const subsection = this.model.fileSections[sectionIndex].subSections[subIndex];
    if (subsection.media[mediaIndex] && subsection.media[mediaIndex].id) {
      this.deleteMedia.push(subsection.media[mediaIndex].id);
      this.deleteMediaChange.emit(this.deleteMedia);
    }
    subsection.media.splice(mediaIndex, 1);
    this.modelChange.emit(this.model);
  }

  getFileUploadText(subsection: any): string {
    const fileCount = subsection.media.length || 0;
    if (fileCount === 0) {
      return "Select File";
    } else if (fileCount === 1) {
      return `1 file selected`;
    } else {
      return `${fileCount} files selected`;
    }
  }

  onPreviewClick(media: any, type: string) {
    if (media.url) {
      window.open(media.url, "_blank");
    }
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  scrollToFileSection(sectionIndex: number): void {
    const el = document.getElementById(`file-section-${sectionIndex}`);
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  /**
   * Handle thumbnail image load event - clear loading state
   */
  onThumbnailImageLoad(sectionIndex: number, subIndex: number, mediaIndex: number): void {
    const subsection = this.model.fileSections[sectionIndex].subSections[subIndex];
    if (subsection && subsection.media && subsection.media[mediaIndex]) {
      subsection.media[mediaIndex].thumbnailLoading = false;
      this.modelChange.emit(this.model);
    }
  }

  /**
   * Handle thumbnail image error event - clear loading state
   */
  onThumbnailImageError(sectionIndex: number, subIndex: number, mediaIndex: number): void {
    const subsection = this.model.fileSections[sectionIndex].subSections[subIndex];
    if (subsection && subsection.media && subsection.media[mediaIndex]) {
      subsection.media[mediaIndex].thumbnailLoading = false;
      this.modelChange.emit(this.model);
    }
  }

  getFileSizeInSubsection(sectionIndex: number, subIndex: number): number {
    const subsection =
      this.model.fileSections[sectionIndex] && this.model.fileSections[sectionIndex].subSections[subIndex];
    if (!subsection || !subsection.media) return 0;

    const maxValidSizeBytes = 100 * 1024 * 1024; // 100MB in bytes

    return (
      subsection.media.reduce((total, media) => {
        const size = media.size;
        console.log("size", size);

        // Validate size: must be a valid number, > 0, and <= 100MB
        if (!size || typeof size !== "number" || size <= 0 || size > maxValidSizeBytes) {
          // Skip invalid sizes to prevent calculation errors
          console.warn("Invalid media size detected and skipped:", {
            fileName: media.fileName,
            size: size,
            mediaId: media.id,
          });
          return total;
        }

        return total + size;
      }, 0) /
      (1024 * 1024)
    );
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  async uploadSingleFileToSubsection(
    file: File,
    uploadKey: string,
    mediaItem: any,
    subsection: any,
    folder: string,
    maxSizeMB: number,
    attempt: number = 1
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const subscription = this.uploadService.uploadFileToS3(folder, file, maxSizeMB).subscribe({
        next: (response: any) => {
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);

            const mediaIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);

            if (mediaIndex !== -1) {
              // Update the media item with the response URL
              subsection.media[mediaIndex] = {
                ...subsection.media[mediaIndex],
                url: response.url,
                uploadKey: null, // Clear uploadKey after completion
              };
              // Clean up tempId
              delete subsection.media[mediaIndex].tempId;

              this.toastr.success(`File ${file.name} uploaded successfully`);
              this.cleanupUpload(uploadKey);
              this.activeUploads.delete(uploadKey);

              // Automatically upload default thumbnail for PDF files (first page)
              if (file.type === "application/pdf" && !subsection.media[mediaIndex].thumbnail) {
                this.uploadDefaultThumbnailForPDF(file, subsection.media[mediaIndex], subsection).catch(error => {
                  console.error("Failed to upload default thumbnail:", error);
                  // Don't show error toast for default thumbnail upload failure
                });
              }

              resolve(response);
            } else {
              console.error(`Could not find media item with tempId: ${mediaItem.tempId}`);
              this.activeUploads.delete(uploadKey);
              reject(new Error("Media item not found after upload"));
            }
          }
        },
        error: error => {
          if (attempt < this.FILE_UPLOAD_MAX_RETRIES) {
            this.activeUploads.delete(uploadKey);
            setTimeout(() => {
              this.uploadSingleFileToSubsection(file, uploadKey, mediaItem, subsection, folder, maxSizeMB, attempt + 1)
                .then(resolve)
                .catch(reject);
            }, this.FILE_UPLOAD_RETRY_DELAY_MS);
            return;
          }

          this.setUploadState(uploadKey, false);

          // Remove the media item on error using tempId
          const itemIndex = subsection.media.findIndex((media: any) => media.tempId === mediaItem.tempId);
          if (itemIndex > -1) {
            subsection.media.splice(itemIndex, 1);
          }

          this.toastr.error(`Failed to upload ${file.name}: ${this.getReadableUploadError(error)}`);
          console.error("Upload error:", error);
          this.cleanupUpload(uploadKey);
          this.activeUploads.delete(uploadKey);
          reject(error);
        },
      });
      this.activeUploads.set(uploadKey, subscription);
    });
  }

  private generateUploadKey(prefix: string, fileName: string, suffix?: string): string {
    const base = `${prefix}_${Date.now()}_${fileName}`;
    return suffix ? `${base}_${suffix}` : base;
  }

  private setUploadState(key: string, isUploading: boolean) {
    this.uploadStates[key] = isUploading;
  }

  private setUploadProgress(key: string, percent: number) {
    this.uploadProgress[key] = percent;
  }

  isUploadInProgress(key: string): boolean {
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    return this.uploadProgress[key] || 0;
  }

  private cleanupUpload(key: string) {
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  private getReadableUploadError(error: any): string {
    if (!error) return "Unknown upload error";
    if (typeof error.message === "string" && error.message.trim() !== "") {
      return error.message;
    }
    if (typeof error.statusText === "string" && error.statusText.trim() !== "") {
      return error.statusText;
    }
    if (typeof error.type === "string" && error.type.trim() !== "") {
      return error.type;
    }
    if (error instanceof ProgressEvent) {
      return "Network error";
    }
    return "Upload failed";
  }

  ngOnDestroy(): void {
    this.activeUploads.forEach((subscription, uploadKey) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
      this.cleanupUpload(uploadKey);
    });
    this.activeUploads.clear();
  }

  async uploadThumbnailForFile(file: File, sectionIndex: number, subIndex: number, mediaIndex: number, event?: any) {
    // Helper function to reset input
    const resetInput = () => {
      if (event && event.target) {
        event.target.value = "";
      }
    };

    if (!file) {
      resetInput();
      return;
    }

    // Validate file type (images only)
    const validImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/heic",
      "image/heif",
    ];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    const isValidType =
      validImageTypes.includes(file.type) ||
      (isHeicByExtension && (!file.type || file.type === "application/octet-stream"));
    if (!isValidType) {
      this.toastr.error(`File ${file.name} must be an image (JPEG, PNG, GIF, WebP, or HEIC)`, "Error");
      resetInput();
      return;
    }

    // Validate file size (10MB limit for thumbnail)
    const maxThumbnailSizeMB = 10;
    if (file.size / 1024 / 1024 > maxThumbnailSizeMB) {
      this.toastr.error(`Thumbnail image ${file.name} exceeds ${maxThumbnailSizeMB}MB limit`, "Error");
      resetInput();
      return;
    }

    // Ensure model structure exists
    if (
      !this.model.fileSections ||
      !this.model.fileSections[sectionIndex] ||
      !this.model.fileSections[sectionIndex].subSections ||
      !this.model.fileSections[sectionIndex].subSections[subIndex] ||
      !this.model.fileSections[sectionIndex].subSections[subIndex].media ||
      !this.model.fileSections[sectionIndex].subSections[subIndex].media[mediaIndex]
    ) {
      this.toastr.error("Invalid section, subsection, or media item", "Error");
      resetInput();
      return;
    }

    const subsection = this.model.fileSections[sectionIndex].subSections[subIndex];
    const mediaItem = subsection.media[mediaIndex];

    try {
      file = await this.heicConverter.convertToJpegIfNeeded(file);

      // Generate unique identifier for thumbnail upload
      const timestamp = Date.now();
      const uploadKey = this.generateUploadKey(
        "thumbnail",
        file.name,
        `section_${sectionIndex}_sub_${subIndex}_media_${mediaIndex}_${timestamp}`
      );

      // Store uploadKey in mediaItem for progress tracking
      mediaItem.thumbnailUploadKey = uploadKey;
      this.setUploadState(uploadKey, true);

      // Upload thumbnail and wait for completion
      await this.uploadSingleThumbnail(file, uploadKey, mediaItem, subsection, "digital/photos", maxThumbnailSizeMB);

      // Clear uploadKey after successful upload
      delete mediaItem.thumbnailUploadKey;
      this.toastr.success(`Thumbnail for ${mediaItem.fileName} uploaded successfully`);
    } catch (error) {
      // Clear uploadKey on error
      if (mediaItem.thumbnailUploadKey) {
        delete mediaItem.thumbnailUploadKey;
      }
      console.error(`Failed to upload thumbnail for ${mediaItem.fileName}:`, error);
      this.toastr.error(`Failed to upload thumbnail: ${error.message || error}`, "Error");
    }

    resetInput();
  }

  async uploadSingleThumbnail(
    file: File,
    uploadKey: string,
    mediaItem: any,
    subsection: any,
    folder: string,
    maxSizeMB: number
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      this.uploadService.uploadFileToS3(folder, file, maxSizeMB).subscribe({
        next: (response: any) => {
          if (response && response.type === "progress") {
            this.setUploadProgress(uploadKey, response.percent);
            return;
          }

          if (response && response.type === "complete") {
            this.setUploadState(uploadKey, false);

            // Update the media item with the thumbnail URL
            const mediaIndex = subsection.media.findIndex((media: any) => media === mediaItem);
            if (mediaIndex !== -1) {
              subsection.media[mediaIndex].thumbnail = response.url;
              // Set loading state - image needs to load
              subsection.media[mediaIndex].thumbnailLoading = true;
              // Clear uploadKey from mediaItem
              if (subsection.media[mediaIndex].thumbnailUploadKey === uploadKey) {
                delete subsection.media[mediaIndex].thumbnailUploadKey;
              }
              this.modelChange.emit(this.model);
              this.cleanupUpload(uploadKey);
              resolve(response);
            } else {
              console.error(`Could not find media item for thumbnail update`);
              reject(new Error("Media item not found after thumbnail upload"));
            }
          }
        },
        error: error => {
          this.setUploadState(uploadKey, false);
          // Clear uploadKey from mediaItem on error
          const mediaIndex = subsection.media.findIndex((media: any) => media === mediaItem);
          if (mediaIndex !== -1 && subsection.media[mediaIndex].thumbnailUploadKey === uploadKey) {
            delete subsection.media[mediaIndex].thumbnailUploadKey;
          }
          this.toastr.error(`Failed to upload thumbnail: ${error.message || error}`);
          console.error("Thumbnail upload error:", error);
          this.cleanupUpload(uploadKey);
          reject(error);
        },
      });
    });
  }

  /**
   * Generate thumbnail from first page of PDF file and upload
   */
  private async uploadDefaultThumbnailForPDF(pdfFile: File, mediaItem: any, subsection: any): Promise<void> {
    try {
      // Load PDF.js library
      const pdfjsLib = await this.loadPDFJSLibrary();

      // Set worker source path
      if (pdfjsLib.GlobalWorkerOptions) {
        pdfjsLib.GlobalWorkerOptions.workerSrc = "./assets/pdfjs/pdf.worker.min.js";
      }

      // Read PDF file as array buffer (FileReader for TS 2.7 / older DOM libs; File.arrayBuffer() not in typings)
      const arrayBuffer = await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = reject;
        reader.readAsArrayBuffer(pdfFile);
      });

      // Load PDF document
      const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
      const pdf = await loadingTask.promise;

      // Get first page
      const page = await pdf.getPage(1);

      // Set scale for thumbnail (adjust as needed)
      const scale = 2.0;
      const viewport = page.getViewport({ scale: scale });

      // Create canvas to render PDF page
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (!context) {
        throw new Error("Failed to get canvas context");
      }
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Render PDF page to canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          blob => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error("Failed to convert canvas to blob"));
            }
          },
          "image/jpeg",
          0.9
        ); // 0.9 quality
      });

      // Create File from blob
      const thumbnailFile = new File([blob], `thumbnail_${pdfFile.name.replace(".pdf", "")}.jpg`, {
        type: "image/jpeg",
      });

      // Generate unique identifier for thumbnail upload
      const timestamp = Date.now();
      const uploadKey = this.generateUploadKey("thumbnail", thumbnailFile.name, `pdf_first_page_${timestamp}`);

      // Store uploadKey in mediaItem for progress tracking
      mediaItem.thumbnailUploadKey = uploadKey;
      this.setUploadState(uploadKey, true);

      // Upload thumbnail and wait for completion
      await this.uploadSingleThumbnail(
        thumbnailFile,
        uploadKey,
        mediaItem,
        subsection,
        "digital/photos",
        10 // maxSizeMB
      );

      // Clear uploadKey after successful upload
      delete mediaItem.thumbnailUploadKey;
    } catch (error) {
      console.error("Failed to generate and upload thumbnail from PDF first page:", error);
      // Clear uploadKey on error
      if (mediaItem.thumbnailUploadKey) {
        delete mediaItem.thumbnailUploadKey;
      }
      throw error;
    }
  }

  /**
   * Load PDF.js library dynamically from assets
   */
  private loadPDFJSLibrary(): Promise<any> {
    return new Promise((resolve, reject) => {
      // Check if already loaded in window
      const win = window as any;
      if (win.pdfjsLib && win.pdfjsLib.getDocument) {
        resolve(win.pdfjsLib);
        return;
      }

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="pdf.min.js"]');
      if (existingScript) {
        // Wait a bit for script to load
        setTimeout(() => {
          if (win.pdfjsLib && win.pdfjsLib.getDocument) {
            resolve(win.pdfjsLib);
          } else {
            reject(new Error("PDF.js script loaded but library not available"));
          }
        }, 100);
        return;
      }

      // Load PDF.js script from assets
      const script = document.createElement("script");
      script.src = "./assets/pdfjs/pdf.min.js";
      script.async = true;

      script.onload = () => {
        // Wait a bit for PDF.js to initialize
        setTimeout(() => {
          if (win.pdfjsLib && win.pdfjsLib.getDocument) {
            resolve(win.pdfjsLib);
          } else {
            // Try alternative: check for PDFJS global
            if (win.PDFJS && win.PDFJS.getDocument) {
              resolve(win.PDFJS);
            } else {
              reject(new Error("PDF.js failed to initialize"));
            }
          }
        }, 100);
      };

      script.onerror = () => {
        reject(new Error("Failed to load PDF.js script from assets"));
      };

      document.head.appendChild(script);
    });
  }
}
